import React from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface Post {
  postId: string;
  title: string;
  url: string | null;
  publishDate: number;
  uniqueReaders: number;
  totalReads: number;
  recipients: number;
  delivered: number;
  opens: number;
  uniqueOpens: number;
  openRate: number;
  clicks: number;
  uniqueClicks: number;
  clickRate: number;
  unsubscribes: number;
  spamReports: number;
}

interface PostsChartProps {
  posts: Post[];
}

const PostsChart: React.FC<PostsChartProps> = ({ posts }) => {
  const [selectedMetrics, setSelectedMetrics] = React.useState<string[]>(['uniqueReaders', 'totalReads', 'uniqueClicks']);

  const metricsOptions = [
    { value: 'uniqueReaders', label: 'Leitores Únicos' },
    { value: 'totalReads', label: 'Total de Leituras' },
    { value: 'recipients', label: 'Destinatários' },
    { value: 'delivered', label: 'Entregues' },
    { value: 'opens', label: 'Aberturas' },
    { value: 'uniqueOpens', label: 'Aberturas Únicas' },
    { value: 'openRate', label: 'Taxa de Abertura (%)' },
    { value: 'clicks', label: 'Cliques' },
    { value: 'uniqueClicks', label: 'Cliques Únicos' },
    { value: 'clickRate', label: 'Taxa de Cliques (%)' },
    { value: 'unsubscribes', label: 'Cancelamentos' },
    { value: 'spamReports', label: 'Denúncias de Spam' }
  ];

  const colors = [
    { borderColor: '#FFCC04', backgroundColor: '#FFE066' },
    { borderColor: '#4A4008', backgroundColor: '#666666' },
    { borderColor: '#000000', backgroundColor: '#333333' },
    { borderColor: '#999999', backgroundColor: '#CCCCCC' }
  ];

  const sortedPosts = [...posts].sort((a, b) => a.publishDate - b.publishDate);

  const data = {
    labels: sortedPosts.map(post => 
      new Date(post.publishDate * 1000).toLocaleDateString('pt-BR')
    ),
    datasets: selectedMetrics.map((metric, index) => ({
      label: metricsOptions.find(opt => opt.value === metric)?.label || metric,
      data: sortedPosts.map(post => post[metric as keyof Post]),
      borderColor: colors[index % colors.length].borderColor,
      backgroundColor: colors[index % colors.length].backgroundColor,
      tension: 0.4,
      pointRadius: 6,
      pointHoverRadius: 8,
      pointBackgroundColor: colors[index % colors.length].borderColor,
      pointBorderColor: '#fff',
      pointBorderWidth: 2,
      yAxisID: metric.includes('Rate') ? 'percentage' : 'absolute'
    }))
  };

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          font: {
            size: 14
          }
        }
      },
      tooltip: {
        backgroundColor: '#f5f5f5',
        titleColor: '#000',
        bodyColor: '#000',
        borderColor: '#ddd',
        borderWidth: 1,
        padding: 12,
        callbacks: {
          label: (context) => {
            const value = context.parsed.y;
            const metric = selectedMetrics[context.datasetIndex];
            const label = metricsOptions.find(opt => opt.value === metric)?.label;
            
            if (metric.includes('Rate')) {
              return `${label}: ${value.toFixed(2)}%`;
            }
            return `${label}: ${value.toLocaleString('pt-BR')}`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          font: {
            size: 12
          }
        }
      },
      absolute: {
        type: 'linear',
        position: 'left',
        beginAtZero: true,
        grid: {
          color: '#e5e5e5'
        },
        ticks: {
          font: {
            size: 12
          },
          callback: (value) => value.toLocaleString('pt-BR')
        }
      },
      percentage: {
        type: 'linear',
        position: 'right',
        beginAtZero: true,
        max: 100,
        grid: {
          display: false
        },
        ticks: {
          font: {
            size: 12
          },
          callback: (value) => `${value}%`
        }
      }
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 p-4 bg-gray-50 rounded-lg">
        {metricsOptions.map((metric) => (
          <button
            key={metric.value}
            onClick={() => {
              if (selectedMetrics.includes(metric.value)) {
                setSelectedMetrics(selectedMetrics.filter(m => m !== metric.value));
              } else if (selectedMetrics.length < 4) {
                setSelectedMetrics([...selectedMetrics, metric.value]);
              }
            }}
            className={`px-3 py-1 rounded-full text-sm transition-colors ${
              selectedMetrics.includes(metric.value)
                ? 'bg-black text-white'
                : 'bg-white border border-gray-300 hover:bg-gray-100'
            }`}
            disabled={selectedMetrics.length >= 4 && !selectedMetrics.includes(metric.value)}
          >
            {metric.label}
          </button>
        ))}
      </div>
      
      <div style={{ height: '400px' }}>
        <Line data={data} options={options} />
      </div>
    </div>
  );
};

export default PostsChart; 